import React, { startTransition, useEffect, useRef, useState } from 'react';

/**
 * Page-level switch (Ödevler / Notlandırma, Program / Hedef Görünümü, …): one track,
 * one pink thumb that SLIDES to the chosen option, like an iOS toggle, instead of the
 * chosen button lighting up. It sits next to the page title.
 *
 * The grey track is the thumb's range; it is a shade darker than the page behind it so the
 * range reads as a groove, not a blank patch.
 *
 * Every option gets the width of the widest label, so the thumb can move by whole
 * columns (translateX(index * 100%)) with no measuring. For a filter inside a page use
 * <FilterPills/>, not this: one sliding thumb per screen keeps the page switch the
 * obvious one.
 */
export interface SwitchOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Thumb colour while this option is selected (6-digit hex). Defaults to the brand pink. */
  color?: string;
}

interface Props<T extends string> {
  value: T;
  options: ReadonlyArray<SwitchOption<T>>;
  onChange: (v: T) => void;
  ariaLabel: string;
  /** `sm` for a switch inside a card; `md` next to a page title. */
  size?: 'md' | 'sm';
  className?: string;
}

function SegmentedSwitch<T extends string>({ value, options, onChange, ariaLabel, size = 'md', className = '' }: Props<T>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  // The thumb follows `local`, which a click sets at once. The page's own update (`onChange`)
  // is a transition: React renders the switch first, so the thumb starts sliding on the click
  // instead of waiting for the whole page behind it (lists, charts) to re-render.
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  const index = Math.max(0, options.findIndex((o) => o.value === local));
  const n = options.length;

  const select = (v: T) => {
    setLocal(v);
    startTransition(() => onChange(v));
  };
  const move = (to: number) => {
    const next = (to + n) % n;
    select(options[next].value);
    refs.current[next]?.focus();
  };

  const accent = options[index]?.color ?? '#e5599b';

  const text = size === 'md' ? 'text-[13px] lg:text-[15px] px-4 lg:px-5 py-1.5 lg:py-2' : 'text-[12px] lg:text-[13px] px-3.5 py-1.5';

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`relative inline-grid bg-[#e4e6ee] rounded-full p-1 ${className}`}
      style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }}
    >
      {/* The thumb: one column wide, parked by index. */}
      <span
        aria-hidden
        className="absolute top-1 bottom-1 left-1 rounded-full transition-[transform,background-color,box-shadow] duration-[280ms] ease-[cubic-bezier(0.3,0.9,0.3,1)] motion-reduce:transition-none"
        style={{
          width: `calc((100% - 8px) / ${n})`,
          transform: `translateX(${index * 100}%)`,
          backgroundColor: accent,
          boxShadow: `0 2px 6px ${accent}59`,
        }}
      />
      {options.map((o, i) => {
        const active = i === index;
        return (
          <button
            key={o.value}
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => select(o.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') { e.preventDefault(); move(i + 1); }
              else if (e.key === 'ArrowLeft') { e.preventDefault(); move(i - 1); }
            }}
            className={`relative z-10 whitespace-nowrap rounded-full font-semibold transition-colors duration-200 motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3973bf] ${text} ${
              active ? 'text-white' : 'text-gray-600 hover:text-[#e5599b]'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedSwitch;
